import React, { useEffect } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Grid, Header, Icon, Segment, Table } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import InfoPopup from '~/components/InfoPopup'

const CoProducts: React.FC = () => {
  const { t } = useTranslation()
  const { control } = useFormContext()

  const { fields: coProductFields, append: appendCoProduct, remove: removeCoProduct, replace } = useFieldArray({
    control,
    name: 'coProducts',
    keyName: 'key'
  })

  const watchCheckedCoProducts = useWatch({ control, name: 'hasCoProducts' })

  useEffect(() => {
    if(!watchCheckedCoProducts) {
      replace([])
      return
    }
    
    if(coProductFields.length === 0) {
      appendCoProduct({
        name: '',
        percent: null
      })
    }
  }, [watchCheckedCoProducts])

  return (
    <>
      <Header attached='top' block>
        {t('Co-products')}
      </Header>
      <Segment attached>
        <Grid>
          <Grid.Row>
            <Grid.Column width={16}>
              <FormFields.Checkbox
                control={control}
                name='hasCoProducts'
                label={t('Are there any co-products of this crop which you use or sell?')}
                checkboxValue={1}
                defaultValue={0}
              />
            </Grid.Column>
          </Grid.Row>

          {watchCheckedCoProducts === 1 && (
            <Grid.Row>
              <Grid.Column width={16}>
                <Table celled>
                  <colgroup>
                    <col style={{ width: '40%' }} />
                    <col style={{ width: '40%' }} />
                    <col style={{ width: '20%' }} />
                  </colgroup>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <div>{t('Type of co-product')}</div>
                          <InfoPopup
                            content={t('For example \'straw\', or \'sugar beet pulp\'')}
                            styleIcon={{ fontSize: '1.2em', color: 'white' }}
                          />
                        </div>
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <div>{t('Value relative to crop')}</div>
                          <InfoPopup
                            styleIcon={{ fontSize: '1.2em', color: 'white' }}
                            content={t('Enter value for co-product as a % relative to primary crop. For example, if Grain €1000, Straw €100, enter 10%.')}
                        />
                        </div>
                      </Table.HeaderCell>
                      <Table.HeaderCell>{t('Actions')}</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {coProductFields.map((field, index) => (
                      <Table.Row key={field.key}>
                        <Table.Cell>
                          <FormFields.Input
                            control={control}
                            name={`coProducts[${index}].name`}
                            withoutAsterisk
                            placeholder={t('Enter co-product name')}
                            rules={{
                              required: true
                            }}
                            noErrorMessage
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <FormFields.Input
                            control={control}
                            name={`coProducts[${index}].percent`}
                            withoutAsterisk
                            placeholder={t('Enter percent')}
                            rules={{
                              required: true
                            }}
                            typeInput='controled'
                            allowDecimals
                            decimalScale={2}
                            noErrorMessage
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Icon
                            name='trash'
                            color='red'
                            style={{ cursor: 'pointer' }}
                            onClick={() => removeCoProduct(index)}
                          />
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
                <div style={{ display: 'flex', justifyContent: 'end', marginTop: '1em' }}>
                  <Button
                    type='button'
                    size='small'
                    icon='plus'
                    style={{ color: 'white' }}
                    content={t('Add Co-product')}
                    onClick={() => appendCoProduct({
                      name: '',
                      percent: null
                    })}
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
          )}
        </Grid>
      </Segment>
    </>
  )
}
export default CoProducts